// libraries
import { NumberInput, NumberInputField } from '@chakra-ui/react';
// Types
import { CustomNumberInputProps } from './CustomNumberInput.types';

/**
 * A custom styled number input field, which optionally can format values as currency.
 *
 * @component
 * @param {CustomNumberInputProps} props - The props for the CustomNumberInput component.
 * @param {string} props.currency - Indicates if the number input should be treated as a currency.
 * @param {'end'|'start'} props.currencyPosition -  String used to add a currency symbol.
 * @param {boolean} props.isDisabled - Determines if the number input is disabled.
 * @param {boolean} props.isInvalid - Indicates if the number input has an invalid value.
 * @param {number} props.max - Maximum allowable value.
 * @param {number} props.maxW - Max width of the input element.
 * @param {number} props.min - Minimum allowable value.
 * @param {number} props.precision - Number of decimal places for the number input.
 * @param {Function} props.setValue - Callback fired when the value changes.
 * @param {number} props.step - The stepping interval for value adjustments.
 * @param {number} props.value - Current value of the number input.
 * @param {number} props.maxLength -  The max length to use in the input field.
 *
 * @returns {ReactElement} The CustomNumberInput component.
 */
export const CustomNumberInput = ({
  isDisabled,
  isInvalid,
  max,
  min,
  precision,
  setValue,
  step,
  value,
  currency,
  currencyPosition = 'start',
  maxW = 28,
  maxLength = 4,
}: CustomNumberInputProps) => {
  const color = isDisabled ? 'border.100' : 'text.faded';
  const fieldStyles = currency && currencyPosition === 'start' ? { pl: 7 } : { pl: 3 };
  const left = currencyPosition === 'start' ? 2 : 'calc(100% - 1.5rem)';
  const restStyles = currency
    ? {
        position: 'relative',
        _before: {
          position: 'absolute',
          content: `"${currency}"`,
          lineHeight: 8,
          left,
          color,
          fontSize: 'sm',
        },
      }
    : {};

  return (
    <NumberInput
      isDisabled={isDisabled}
      allowMouseWheel
      isInvalid={isInvalid}
      errorBorderColor='text.error'
      precision={precision}
      step={step}
      min={min}
      max={max}
      maxW={maxW}
      keepWithinRange={false}
      clampValueOnBlur={false}
      value={value}
      onChange={setValue}
      border='section'
      borderRadius='md'
      size='sm'
      sx={restStyles}
    >
      <NumberInputField
        h={8}
        sx={fieldStyles}
        border='none'
        maxLength={maxLength}
        paddingInlineEnd={0}
        _focusVisible={{
          _invalid: {
            boxShadow: 'errorOutline',
          },
          boxShadow: 'outline',
        }}
      />
    </NumberInput>
  );
};
